<template>
  <div id="app">
    <div class="app-main">
      <b-navbar type="dark" variant="dark" v-if="userReady && userRole === 'Admin'">
        <b-navbar-brand href="#">Memo Fast</b-navbar-brand>
        <b-navbar-nav class="ms-auto">
          <b-nav-item disabled>
            👤 Rôle : <strong>{{ userRole }}</strong>
          </b-nav-item>
        </b-navbar-nav>
      </b-navbar>

      <!-- Always allow access to legal pages, even before terms acceptance -->
      <router-view v-if="isLegalRoute" />
      <router-view v-else-if="isPublicRoute" />

      <TermsGate
        v-else-if="termsRequired"
        :currentVersion="termsVersion"
        @accepted="onTermsAccepted"
      />

      <router-view v-else-if="userReady == true" />

      <div id="waiting" v-else-if="userReady == false">
        <div class="waiting-card">
          <div class="waiting-logo">MemoFast</div>
          <p class="waiting-text">Connexion en cours, préparation de votre espace...</p>
          <div id="loader" class="text-center">
            <b-spinner variant="primary" label="Chargement"></b-spinner>
          </div>
        </div>
      </div>
    </div>

    <LegalFooter v-if="!isPublicRoute && !isLegalRoute" />
    <SearchPalette v-if="userReady" ref="searchPalette" />
  </div>
</template>

<script>
import { createUserSession } from '@/services/authApi'
import { getTermsStatus } from '@/services/termsApi'
import TermsGate from '@/components/Pages/TermsGate'
import LegalFooter from '@/components/Layout/LegalFooter'
import SearchPalette from '@/components/Common/SearchPalette'
import { getCurrentUserRole, isAuthenticated, logout, refreshSessionUserId } from '@/services/authManager'
import { getUiState } from '@/stores/uiStore'

let tourServicePromise
const loadTourService = async () => {
  if (!tourServicePromise) {
    tourServicePromise = import('@/services/TourService').then((module) => module.default)
  }
  return tourServicePromise
}

export default {
  name: 'App',
  components: { TermsGate, LegalFooter, SearchPalette },
  data () {
    return {
      userReady: false,
      termsRequired: false,
      termsVersion: 'unknown',
      uiState: getUiState()
    }
  },
  computed: {
    isLegalRoute () {
      return [
        'LegalCgu',
        'LegalPrivacy',
        'LegalNotice',
        'LegalCookies'
      ].includes(this.$route?.name)
    },
    isPublicRoute () {
      return ['Landing', 'Plans', 'Documentation'].includes(this.$route?.name)
    },
    userRole () {
      return getCurrentUserRole()
    }
  },
  mounted () {
    document.addEventListener('keydown', this.handleGlobalKeydown)
  },
  beforeUnmount () {
    document.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  methods: {
    handleGlobalKeydown (e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        this.$refs.searchPalette?.show()
      }
    },
    async maybeAutostartTour () {
      if (!this.userReady) return
      if (this.$route.name !== 'HomePage') return

      const tourService = await loadTourService()
      if (tourService.isCompleted()) return

      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$route.name === 'HomePage') {
            tourService.start()
          }
        }, 500)
      })
    },
    async checkTerms () {
      const status = await getTermsStatus()
      this.termsVersion = status.current_version || 'unknown'
      this.termsRequired = status.accepted === false
      return status
    },

    async onTermsAccepted () {
      this.termsRequired = false
      this.userReady = true
    }
  },
  async created () {
    await this.$router.isReady()

    if (this.isPublicRoute || this.isLegalRoute) {
      this.userReady = true
      return
    }

    if (!isAuthenticated()) {
      this.userReady = false
      return
    }

    try {
      await createUserSession()
      refreshSessionUserId()

      // Block app until Terms/CGU are accepted for the current version.
      await this.checkTerms()
      if (this.termsRequired) {
        this.userReady = false
        return
      }

      this.userReady = true
    } catch (err) {
      console.error("Erreur CreateUserSession", err)
      this.userReady = false

      // If backend denies access (ex: account erased), avoid leaving the UI stuck indefinitely.
      const raw = err?.response?.data?.error
      const msg =
        typeof raw === 'string'
          ? raw
          : (raw?.error || (raw ? JSON.stringify(raw) : ''))

      if (msg && msg.toLowerCase().includes('compte supprim')) {
        this.$toasted?.show("Votre compte a été supprimé. Déconnexion en cours...", {
          position: 'bottom-right',
          type: 'error',
          duration: 4000
        })
        try { await logout({ redirectUri: window.location.origin }) } catch (e) { /* noop */ }
      }
    }
  }
  ,
  watch: {
    'uiState.tourAutostartNonce' () {
      this.maybeAutostartTour()
    }
  }
}
</script>

<style>
@font-face {
  font-family: "SansForgetica";
  src: local("SansForgetica"),
   url(./fonts/SansForgetica-Regular.otf) format("truetype");
}
#app {
  font-family: var(--font-family-base);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-main {
  flex: 1;
}

/* Remove Bootstrap container side gutters on top-level pages */
#app > .container-fluid {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* Shared responsive page gutters for redesigned screens */
.app-page-shell {
  padding-left: 10px;
  padding-right: 10px;
}

@media (min-width: 768px) {
  .app-page-shell {
    padding-left: 16px;
    padding-right: 16px;
  }
}

@media (min-width: 1200px) {
  .app-page-shell {
    padding-left: 24px;
    padding-right: 24px;
  }
}

#waiting {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--s-5);
  background: linear-gradient(180deg, var(--color-primary-soft) 0%, var(--color-bg) 100%);
}

#loader {
  margin-top: var(--s-4);
}

.waiting-card {
  width: 100%;
  max-width: 440px;
  background: var(--color-surface);
  border-radius: 24px;
  padding: var(--s-6) var(--s-5);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--color-border);
}

.waiting-logo {
  font-family: var(--font-family-logo);
  font-size: var(--font-size-3xl);
  color: var(--color-primary);
}

.waiting-text {
  margin-top: var(--s-4);
  margin-bottom: 0;
  color: var(--color-text-muted);
  font-weight: var(--font-weight-medium);
}
</style>
